import { supabase } from '../supabaseClient'
import type { ImportPreview, UnresolvedRef } from './importPreview'

export interface ApplyImportResult {
  success: boolean
  error_code?: string
  error_detail?: string
  companies_created?: number
  companies_updated?: number
  hotels_created?: number
  room_types_created?: number
  allocations_applied?: number
  guests_written?: number
}

export async function applyImport(opts: {
  eventId: string
  companyId: string | null
  fileName: string
  workflow: 'company' | 'master' | 'reimport'
  preview: ImportPreview
  bypassDuplicates: boolean
}): Promise<ApplyImportResult> {
  const { eventId, preview } = opts

  const newHotels = preview.unresolved.filter((u) => u.kind === 'hotel' && u.resolution.type === 'create')
  const newRoomTypes = preview.unresolved.filter((u) => u.kind === 'room_type' && u.resolution.type === 'create')

  const { data: job, error: jobError } = await supabase
    .from('import_jobs')
    .insert({ event_id: eventId, company_id: opts.companyId, workflow: opts.workflow, file_name: opts.fileName, status: 'validated', preview: preview as any })
    .select('id')
    .single()
  if (jobError || !job) return { success: false, error_code: 'could_not_create_import_job', error_detail: jobError?.message }

  const { data, error } = await supabase.rpc('admin_apply_import', {
    p_import_job_id: job.id,
    p_event_id: eventId,
    p_companies: preview.companies.map((c) => ({
      code: c.code,
      name: c.name,
      contact_person: c.contact_person,
      contact_mobile: c.contact_mobile,
      contact_email: c.contact_email,
      notes: c.notes,
      deadline_at: c.deadline_at,
      status: c.status,
    })),
    p_new_hotels: newHotels.map((h) => ({ tmp_id: h.key, name: h.originalName })),
    p_new_room_types: newRoomTypes.map((r) => ({ tmp_id: r.key, name: r.originalName, default_capacity: 1 })),
    p_allocations: preview.allocations.map((a) => {
      const hotel = resolvedRefFor(preview, a.hotel_name, 'hotel')
      const roomType = resolvedRefFor(preview, a.room_type_name, 'room_type')
      return {
        company_code: a.company_code,
        hotel_id: hotel?.resolution.type === 'existing' ? hotel.resolution.id : null,
        tmp_hotel_id: hotel?.resolution.type === 'create' ? hotel.key : null,
        room_type_id: roomType?.resolution.type === 'existing' ? roomType.resolution.id : null,
        tmp_room_type_id: roomType?.resolution.type === 'create' ? roomType.key : null,
        room_count: a.room_count,
        capacity_per_room: a.capacity_per_room,
        check_in: a.check_in,
        check_out: a.check_out,
        notes: a.notes,
      }
    }),
    p_guests: preview.guests
      .filter((g) => g.full_name)
      .map((g) => {
        const hotel = resolvedRefFor(preview, g.hotel_name, 'hotel')
        const roomType = resolvedRefFor(preview, g.room_type_name, 'room_type')
        return {
          guest_id: g.guest_id,
          company_code: g.company_code,
          hotel_id: hotel?.resolution.type === 'existing' ? hotel.resolution.id : null,
          tmp_hotel_id: hotel?.resolution.type === 'create' ? hotel.key : null,
          room_type_id: roomType?.resolution.type === 'existing' ? roomType.resolution.id : null,
          tmp_room_type_id: roomType?.resolution.type === 'create' ? roomType.key : null,
          room_slot: g.room_slot,
          guest_slot: g.guest_slot,
          full_name: g.full_name,
          mobile: g.mobile,
          email: g.email,
          check_in: g.check_in,
          check_out: g.check_out,
          notes: g.notes,
        }
      }),
    p_bypass_duplicates: opts.bypassDuplicates,
  })

  if (error) return { success: false, error_code: error.message, error_detail: (error as any).details }
  return data as ApplyImportResult
}

function resolvedRefFor(preview: ImportPreview, name: string, kind: 'hotel' | 'room_type'): UnresolvedRef | undefined {
  return preview.unresolved.find((u) => u.key === `${kind}:${name.toLowerCase()}`)
}

/**
 * For hotel/room-type names that already match an existing record exactly,
 * there's no "unresolved" entry (buildImportPreview only tracks the ones
 * that need admin attention) -- so we resolve those directly by name here,
 * rather than relying on the unresolved list for already-known names.
 */
export function attachKnownIds(preview: ImportPreview, hotels: { id: string; name: string }[], roomTypes: { id: string; name: string }[]): ImportPreview {
  const hotelByName = new Map(hotels.map((h) => [h.name.toLowerCase(), h.id]))
  const rtByName = new Map(roomTypes.map((r) => [r.name.toLowerCase(), r.id]))

  const unresolved = [...preview.unresolved]
  const known = new Set(unresolved.map((u) => u.key))

  for (const a of preview.allocations) {
    for (const [name, kind, map] of [
      [a.hotel_name, 'hotel', hotelByName],
      [a.room_type_name, 'room_type', rtByName],
    ] as const) {
      const key = `${kind}:${name.toLowerCase()}`
      if (!known.has(key) && map.has(name.toLowerCase())) {
        unresolved.push({ key, originalName: name, kind, resolution: { type: 'existing', id: map.get(name.toLowerCase())! } })
        known.add(key)
      }
    }
  }
  for (const g of preview.guests) {
    for (const [name, kind, map] of [
      [g.hotel_name, 'hotel', hotelByName],
      [g.room_type_name, 'room_type', rtByName],
    ] as const) {
      const key = `${kind}:${name.toLowerCase()}`
      if (!known.has(key) && map.has(name.toLowerCase())) {
        unresolved.push({ key, originalName: name, kind, resolution: { type: 'existing', id: map.get(name.toLowerCase())! } })
        known.add(key)
      }
    }
  }

  return { ...preview, unresolved }
}
